import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  GetSubscriptionResponse,
  PostSubscriptionRequest,
} from '@toggle/toggle';

import { postSubscription } from '~/services/event-alerts/event-alerts-service';
import {
  optimisticQueriesUpdate,
  optimisticQueryUpdate,
} from '~/utils/optimistic-updates/optimistic-updates';

import {
  EventAlertsPaginatedResponse,
  QUERY_KEY_EVENT_ALERT_SUBSCRIPTION,
  QUERY_KEY_EVENT_ALERTS,
} from './event-alerts-query-keys';

// When we update the subscription state of an alert, we need to optimistically update the cached subscription
// and alerts data on the FE. Once the network call is complete, we force a refetch on the getSubscription query
export const usePostSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: PostSubscriptionRequest) => postSubscription(body),
    onMutate: async (body: PostSubscriptionRequest) => {
      const newStatus = body.track ? 'active' : 'stopped';

      const alertSubscriptionQueryKey = [
        QUERY_KEY_EVENT_ALERT_SUBSCRIPTION,
        body.source_id,
        body.source_type,
      ];

      const { previousData: previousSubscriptionQuery } =
        await optimisticQueryUpdate<GetSubscriptionResponse>({
          queryClient,
          queryKey: alertSubscriptionQueryKey,
          updateFn: oldData => ({
            ...oldData,
            tracking_status: newStatus,
          }),
        });

      const { previousData: previousAlertQueries } =
        await optimisticQueriesUpdate<EventAlertsPaginatedResponse>({
          queryClient,
          queryKey: [QUERY_KEY_EVENT_ALERTS],
          updateFn: oldData => ({
            ...oldData,
            pages: oldData.pages.map(page => ({
              ...page,
              alerts:
                page.alerts?.map((alert: Alert) =>
                  alert.source_id === body.source_id &&
                  alert.source_type === body.source_type
                    ? { ...alert, tracking_status: newStatus }
                    : alert
                ) ?? [],
            })),
          }),
        });

      return {
        previousAlertQueries,
        previousSubscriptionQuery,
        alertSubscriptionQueryKey,
      };
    },
    onError: (_err, _body, context) => {
      if (context?.previousSubscriptionQuery) {
        queryClient.setQueryData(
          context.alertSubscriptionQueryKey,
          context.previousSubscriptionQuery
        );
      }

      if (context?.previousAlertQueries) {
        context.previousAlertQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEY_EVENT_ALERT_SUBSCRIPTION,
          variables.source_id,
          variables.source_type,
        ],
      });
    },
  });
};
